import { edText } from '@theme/ed-translations';
import { StandardEvents } from '@shopify/events';

/** Server-rendered metafields are authoritative. Never resolve compatibility in browser-only data. */
class EdProductContent extends HTMLElement {
  #controller;
  #generation = 0;
  #pending = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#select, options);
    // Block a stale accessory form immediately while its main-product selection is resolving.
    this.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('[data-ed-show-specs]') : null;
      if (link) this.closest('.shopify-section')?.querySelector('ed-product-tabs')?.selectKey('specifications');
    }, options);
    this.addEventListener('submit', (event) => {
      if (this.#pending) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { ...options, capture: true });
  }

  disconnectedCallback() { this.#controller?.abort(); }

  #select = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const picker = event.target.closest('variant-picker');
    if (!picker || picker.dataset.productId !== this.dataset.productId) return;
    const generation = ++this.#generation;
    this.#pending = true;
    this.setAttribute('aria-busy', 'true');
    this.querySelector('[data-ed-load-error]').hidden = true;
    this.querySelectorAll('button[type="submit"]').forEach((button) => { button.disabled = true; });
    this.querySelectorAll('input[data-ed-optional-variant-id]').forEach((input) => { input.disabled = true; });
    try {
      const { detail } = await event.promise;
      if (generation !== this.#generation || !this.isConnected) return;
      if (!detail?.html) throw new Error('Missing rendered product information');
      if (!detail.newProduct && detail.productId !== this.dataset.productId) return;
      const source = detail.html.getElementById(this.id);
      const incoming = source?.querySelector('[data-ed-content]');
      if (!incoming) throw new Error('Missing Easy Drain block');
      const activeTab = this.querySelector('ed-product-tabs')?.dataset.activeKey;
      const current = this.querySelector('[data-ed-content]');
      // Replace only this block's content. Native main product form and picker stay untouched.
      current.replaceChildren(...Array.from(incoming.childNodes).map((node) => document.importNode(node, true)));
      const tabs = this.querySelector('ed-product-tabs');
      if (tabs && activeTab) tabs.selectKey?.(activeTab);
      if (detail.newProduct) this.dataset.productId = detail.newProduct.id;
      this.#pending = false;
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (generation !== this.#generation || error?.name === 'AbortError') return;
      // Keep old accessory forms blocked; they no longer describe the selected product variant.
      this.querySelector('[data-ed-load-error]').hidden = false;
      if (this.dataset.content === 'purchase') this.querySelector('[data-ed-content]').replaceChildren();
      this.removeAttribute('aria-busy');
    }
  };
}

/** Native details without JS; keyboard-accessible tabs at every width, matching the supplied prototype. */
class EdProductTabs extends HTMLElement {
  #controller;
  #media = window.matchMedia('(min-width: 0px)');
  #items = [];

  connectedCallback() {
    this.#controller = new AbortController();
    this.#items = Array.from(this.querySelectorAll('details[data-tab-key]'));
    if (!this.#items.length) { this.hidden = true; return; }
    const nav = this.querySelector('[data-tabs-nav]');
    nav.replaceChildren();
    this.#items.forEach((details, index) => {
      const summary = details.querySelector('summary');
      const panel = details.querySelector('[data-tab-panel]');
      const button = document.createElement('button');
      button.type = 'button';
      button.id = `${panel.id}-tab`;
      button.textContent = summary.textContent;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panel.id);
      button.dataset.key = details.dataset.tabKey;
      button.addEventListener('click', () => this.#select(index));
      nav.append(button);
    });
    nav.addEventListener('keydown', (event) => {
      const current = Array.from(nav.children).indexOf(event.target);
      const last = this.#items.length - 1;
      let next = current;
      if (event.key === 'ArrowRight') next = current === last ? 0 : current + 1;
      else if (event.key === 'ArrowLeft') next = current === 0 ? last : current - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      else return;
      event.preventDefault();
      this.#select(next);
      nav.children[next]?.focus();
    }, { signal: this.#controller.signal });
    this.#media.addEventListener('change', this.#layout, { signal: this.#controller.signal });
    this.#layout();
  }

  disconnectedCallback() { this.#controller?.abort(); }

  selectKey(key) {
    const index = this.#items.findIndex((item) => item.dataset.tabKey === key);
    if (index >= 0) this.#select(index);
  }

  #select(index) {
    const buttons = this.querySelector('[data-tabs-nav]').children;
    this.dataset.activeKey = this.#items[index]?.dataset.tabKey ?? '';
    this.#items.forEach((details, itemIndex) => {
      const selected = index === itemIndex;
      buttons[itemIndex].setAttribute('aria-selected', String(selected));
      buttons[itemIndex].tabIndex = selected ? 0 : -1;
      if (this.#media.matches) details.open = selected;
    });
  }

  #layout = () => {
    const desktop = this.#media.matches;
    this.toggleAttribute('data-desktop-tabs', desktop);
    this.querySelector('[data-tabs-nav]').hidden = !desktop;
    this.#items.forEach((details) => {
      const panel = details.querySelector('[data-tab-panel]');
      const summary = details.querySelector('summary');
      summary.hidden = desktop;
      if (desktop) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', `${panel.id}-tab`);
        panel.tabIndex = 0;
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
        panel.removeAttribute('tabindex');
        details.open = true;
      }
    });
    const active = this.#items.findIndex((item) => item.dataset.tabKey === this.dataset.activeKey);
    this.#select(Math.max(0, active));
  };
}

/** Feedback only; all actual additions remain native Horizon product form submissions. */
class EdProductFeedback extends HTMLElement {
  static #active;
  #controller;
  #formId;
  #operation = 0;
  #panel;
  #message;
  #cart;
  #dismiss;
  #title;

  connectedCallback() {
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.#panel = this.querySelector('[data-feedback-panel]');
    this.#message = this.querySelector('[data-feedback-message]');
    this.#cart = this.querySelector('[data-feedback-cart]');
    this.#dismiss = this.querySelector('[data-feedback-dismiss]');
    this.#title = document.createElement('strong');
    this.#title.className = 'ed-product__notice-title';
    const copy = document.createElement('div');
    copy.className = 'ed-product__notice-copy';
    copy.setAttribute('role', 'status');
    copy.setAttribute('aria-live', 'polite');
    this.#message.removeAttribute('role');
    this.#message.removeAttribute('aria-live');
    copy.append(this.#title, this.#message, this.#cart);
    this.#panel.replaceChildren(copy, this.#dismiss);
    this.#dismiss.setAttribute('aria-label', edText('notice_close'));
    this.#dismiss.textContent = '×';
    if (typeof this.#panel.showPopover === 'function') this.#panel.setAttribute('popover', 'manual');
    this.closest('.shopify-section')?.addEventListener(StandardEvents.cartLinesUpdate, this.#cartUpdate, options);
    this.#dismiss.addEventListener('click', () => this.#hide(true), options);
    // A centered top-layer toast may sit outside the cart panel's rectangle.
    // Its own controls must not be mistaken for a click on the cart backdrop.
    this.#panel.addEventListener('click', (event) => event.stopPropagation(), options);
    const drawerChanged = (event) => {
      if (event.target?.id !== 'cart-drawer' || this.#panel.hidden) return;
      // The native dialog must enter the top layer before this non-modal status popover.
      queueMicrotask(() => { if (this.isConnected && !this.#panel.hidden) this.#present(true); });
    };
    document.addEventListener('theme-drawer:open', drawerChanged, options);
    document.addEventListener('theme-drawer:close', (event) => {
      if (event.target?.id === 'cart-drawer') this.#hide(false);
    }, options);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#hide(false);
  }

  #hide(restoreFocus) {
    ++this.#operation;
    if (restoreFocus && this.#panel?.contains(document.activeElement)) {
      const cartDialog = document.querySelector('#cart-drawer dialog[open]');
      const form = this.#formId && document.getElementById(this.#formId);
      const target = cartDialog?.querySelector('.theme-drawer__close-button')
        ?? form?.querySelector('button[type="submit"]:not([disabled]), input:not([type="hidden"]):not([disabled])')
        ?? this.closest('.shopify-section')?.querySelector('variant-picker input:checked, variant-picker select');
      target?.focus({ preventScroll: true });
    }
    if (typeof this.#panel?.showPopover === 'function' && this.#panel.matches(':popover-open')) this.#panel.hidePopover();
    if (this.#panel) {
      this.#panel.hidden = true;
      if (this.#panel.parentElement !== this) this.append(this.#panel);
    }
    if (EdProductFeedback.#active === this) EdProductFeedback.#active = null;
  }

  #present(reorder = false) {
    const panel = this.#panel;
    const host = document.querySelector('#cart-drawer dialog[open]') ?? this;
    // A popover outside a modal dialog can remain inert. Keep it in the modal's
    // accessible subtree while open; it is still a status message, never a dialog.
    const supportsPopover = typeof panel.showPopover === 'function';
    if (supportsPopover && panel.matches(':popover-open') && (reorder || panel.parentElement !== host)) panel.hidePopover();
    if (panel.parentElement !== host) host.append(panel);
    panel.hidden = false;
    if (supportsPopover && !panel.matches(':popover-open')) panel.showPopover();
  }

  #cartUpdate = async (event) => {
    if (event.action !== 'add' || !(event.target instanceof Element)) return;
    const component = event.target.closest('product-form-component');
    if (!component) return;
    const ownBlock = this.closest('ed-product-content');
    if (this.dataset.content === 'compatibility') {
      if (!ownBlock.contains(component)) return;
    } else if (component.dataset.productId !== this.dataset.productId) return;
    const operation = ++this.#operation;
    this.#formId = component.querySelector('form')?.id;
    // Capture before waiting: the buyer may choose a different variant while this add finishes.
    const inputVariantId = String(component.querySelector('input[name="id"]')?.value ?? '');
    // Queued native adds can submit an earlier variant than the current hidden input.
    const submittedVariantId = String(event.lines?.[0]?.merchandiseId ?? inputVariantId).split('/').pop();
    this.#show(edText('feedback_processing'), false, edText('feedback_adding'), 'pending');
    try {
      const { detail } = await event.promise;
      if (operation !== this.#operation || !this.isConnected) return;
      if (detail?.didError) {
        this.#show(edText('feedback_incomplete'), true);
      } else {
        const addedItem = Array.isArray(detail?.items)
          ? detail.items.find((item) => String(item.variant_id ?? item.id) === submittedVariantId)
          : null;
        if (addedItem) {
          const variantTitle = addedItem.product_has_only_default_variant || addedItem.variant_title === 'Default Title'
            ? '' : addedItem.variant_title;
          const label = [addedItem.product_title || addedItem.title, variantTitle, addedItem.sku && edText('sku_value', { sku: addedItem.sku })]
            .filter(Boolean).join(' · ');
          this.#show(`${label}.`, false, edText('feedback_added'));
        } else {
          this.#show(edText('feedback_processed'), true);
        }
      }
    } catch {
      if (operation !== this.#operation || !this.isConnected) return;
      // A lost response can follow a successful server write. Do not invite blind duplicate retries.
      this.#show(edText('feedback_unconfirmed'), true);
    }
  };

  #show(message, cart = false, title = edText('feedback_check_cart'), state = 'success') {
    if (EdProductFeedback.#active && EdProductFeedback.#active !== this) EdProductFeedback.#active.#hide(false);
    EdProductFeedback.#active = this;
    this.#title.textContent = title;
    this.#message.textContent = message;
    this.#cart.hidden = !cart;
    this.#panel.dataset.feedbackState = cart ? 'warning' : state;
    this.#present();
  }
}

if (!customElements.get('ed-product-content')) customElements.define('ed-product-content', EdProductContent);
if (!customElements.get('ed-product-tabs')) customElements.define('ed-product-tabs', EdProductTabs);
if (!customElements.get('ed-product-feedback')) customElements.define('ed-product-feedback', EdProductFeedback);
